import React, { useEffect, useRef, useState } from "react";
import {
  ShieldCheck, User, AlertCircle, Plus, FileText, CheckCircle2, Activity, Trash2, ImageIcon, X, Send,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Human labels for scorecard markers (matches backend LAB_MARKERS keys)
const MARKER_LABELS = {
  egfr: "eGFR", creatinine: "Creatinine", bun: "BUN (Blood urea)", acr: "Urine ACR",
  potassium: "Potassium", phosphorus: "Phosphorus", calcium: "Calcium", pth: "PTH (Parathyroid)",
  hemoglobin: "Hemoglobin", albumin: "Albumin", bicarbonate: "Bicarbonate",
  sodium: "Sodium", vitamin_d: "Vitamin D", uric_acid: "Uric acid",
};
const STATUS_STYLE = {
  normal: { c: "#4ADE80", bg: "rgba(74,222,128,0.10)", brd: "rgba(74,222,128,0.35)", label: "In range" },
  watch:  { c: "#FBBF24", bg: "rgba(251,191,36,0.10)", brd: "rgba(251,191,36,0.35)", label: "Watch" },
  high:   { c: "#F87171", bg: "rgba(248,113,113,0.10)", brd: "rgba(248,113,113,0.35)", label: "Out of range" },
  low:    { c: "#60A5FA", bg: "rgba(96,165,250,0.10)", brd: "rgba(96,165,250,0.35)", label: "Below range" },
  unknown:{ c: "#A3ACA7", bg: "rgba(163,172,167,0.08)", brd: "rgba(163,172,167,0.25)", label: "—" },
};

function Scorecard({ items, meta, onClose, onShare }) {
  const [sharing, setSharing] = useState(false);
  if (!items || items.length === 0) return null;
  const alreadyShared = !!meta?.shared_at;
  const share = async () => {
    if (!meta?.id || alreadyShared) return;
    setSharing(true);
    try { await onShare(meta.id); } finally { setSharing(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="lab-scorecard-modal">
      <div className="ros-card max-w-3xl w-full max-h-[85vh] overflow-y-auto">
        <div className="flex items-start justify-between border-b border-[#272E2B] pb-4 mb-5">
          <div>
            <div className="ros-label">Plain-English Lab Scorecard</div>
            <div className="font-heading text-xl text-white mt-1">{meta?.lab_name || "Your lab report"}</div>
            <div className="text-xs text-[#A3ACA7] mt-1">Report date: {meta?.report_date}</div>
            {alreadyShared && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#4ADE80] bg-[#4ADE80]/10 border border-[#4ADE80]/30 px-2 py-1 rounded-full">
                <CheckCircle2 size={12} /> Shared with your care team on {meta.shared_at.slice(0, 10)}
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-[#A3ACA7] hover:text-white p-1"
                  data-testid="lab-scorecard-close">
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((it) => {
            const s = STATUS_STYLE[it.status] || STATUS_STYLE.unknown;
            return (
              <div key={it.name}
                   data-testid={`scorecard-item-${it.name}`}
                   className="rounded-xl p-4 border transition-colors"
                   style={{ background: s.bg, borderColor: s.brd }}>
                <div className="flex items-center justify-between">
                  <div className="font-heading text-white">{MARKER_LABELS[it.name] || it.name}</div>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                        style={{ color: s.c, background: `${s.c}22`, border: `1px solid ${s.c}66` }}>
                    {s.label}
                  </span>
                </div>
                <div className="flex items-end gap-2 mt-2">
                  <div className="ros-metric text-2xl" style={{ color: s.c }}>
                    {it.value ?? "—"}
                  </div>
                  <div className="text-xs text-[#A3ACA7] pb-1">{it.unit}</div>
                </div>
                {it.reference_range && (
                  <div className="text-[11px] text-[#A3ACA7] mt-1">Normal: {it.reference_range}</div>
                )}
                <div className="text-xs text-white/80 mt-2 leading-relaxed">
                  {it.plain_english}
                </div>
              </div>
            );
          })}
        </div>

        {/* Share action bar */}
        {meta?.id && (
          <div className="mt-6 border-t border-[#272E2B] pt-4 flex flex-col md:flex-row md:items-center gap-3 justify-between">
            <div className="text-xs text-[#A3ACA7] max-w-md leading-relaxed">
              Sending this scorecard adds it to your nephrologist&apos;s inbox and to your next Clinical Export so they see the same plain-English breakdown you do.
            </div>
            <button
              onClick={share}
              disabled={sharing || alreadyShared}
              data-testid="scorecard-share-btn"
              className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 font-semibold transition-colors shrink-0 ${
                alreadyShared
                  ? "bg-[#4ADE80]/10 text-[#4ADE80] border border-[#4ADE80]/40 cursor-default"
                  : "bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90"
              } disabled:opacity-60`}
            >
              {alreadyShared ? <><CheckCircle2 size={16} /> Shared</> :
                <><Send size={16} /> {sharing ? "Sharing…" : "Share with care team"}</>}
            </button>
          </div>
        )}

        <div className="mt-5 text-[11px] text-[#A3ACA7] leading-relaxed border-t border-[#272E2B] pt-4">
          This scorecard is generated automatically by Med Nexus AI to help you understand your labs. It is not a diagnosis — always review out-of-range values with your nephrology team.
        </div>
      </div>
    </div>
  );
}

/**
 * Combines eGFR and ACR readings into a single trust-marker table.
 * Fetches from /api/tracker/egfr and /api/tracker/acr, supports manual entry
 * and PDF/image upload (parsed via Claude on the backend, marked as lab_verified,
 * and returns a plain-English color-coded scorecard).
 */
export default function LabTracker() {
  const [egfrList, setEgfrList] = useState([]);
  const [acrList, setAcrList] = useState([]);
  const [scorecards, setScorecards] = useState([]);
  const [newEgfr, setNewEgfr] = useState("");
  const [newAcr, setNewAcr] = useState("");
  const [newDate, setNewDate] = useState(new Date().toISOString().slice(0, 10));
  const [uploading, setUploading] = useState(false);
  const [scorecardModal, setScorecardModal] = useState(null); // { items, lab_name, report_date }
  const fileRef = useRef(null);

  const load = async () => {
    const [egfrRes, acrRes, scRes] = await Promise.all([
      api.get("/tracker/egfr"),
      api.get("/tracker/acr"),
      api.get("/tracker/scorecards"),
    ]);
    setEgfrList(egfrRes.data);
    setAcrList(acrRes.data);
    setScorecards(scRes.data || []);
  };

  useEffect(() => { load(); }, []);

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!newEgfr && !newAcr) return;
    try {
      if (newEgfr) {
        await api.post("/tracker/egfr", {
          date: newDate,
          egfr: parseFloat(newEgfr),
          source: "patient_reported",
        });
      }
      if (newAcr) {
        await api.post("/tracker/acr", {
          date: newDate,
          acr: parseFloat(newAcr),
          source: "patient_reported",
        });
      }
      setNewEgfr("");
      setNewAcr("");
      toast.success("Manual reading saved");
      await load();
    } catch (err) {
      toast.error("Could not save reading");
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await api.post("/tracker/upload-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const parts = [];
      if (resp.data.egfr) parts.push(`eGFR ${resp.data.egfr.egfr}`);
      if (resp.data.acr) parts.push(`ACR ${resp.data.acr.acr}`);
      const sc = resp.data.scorecard || [];
      if (parts.length) {
        toast.success(`Lab-verified: ${parts.join(", ")} from ${resp.data.lab_name}`);
      } else if (sc.length) {
        toast.success(`Scorecard ready: ${sc.length} markers extracted from ${resp.data.lab_name}`);
      }
      if (sc.length) {
        setScorecardModal({ id: resp.data.scorecard_id,
                            items: sc, lab_name: resp.data.lab_name,
                            report_date: resp.data.report_date });
      }
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Upload failed: ${detail}`);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // Merge into unified rows keyed by date
  const rowMap = new Map();
  for (const r of egfrList) {
    const key = `${r.date}|egfr`;
    rowMap.set(key, { key, date: r.date, egfr: r, acr: null });
  }
  for (const r of acrList) {
    const key = `${r.date}|acr`;
    const eKey = `${r.date}|egfr`;
    if (rowMap.has(eKey)) {
      rowMap.get(eKey).acr = r;
    } else {
      rowMap.set(key, { key, date: r.date, egfr: null, acr: r });
    }
  }
  const rows = Array.from(rowMap.values()).sort((a, b) => (a.date < b.date ? 1 : -1));

  const deleteReading = async (kind, id) => {
    await api.delete(`/tracker/${kind}/${id}`);
    await load();
  };

  return (
    <div className="space-y-8" data-testid="lab-tracker-page">
      {scorecardModal && (
        <Scorecard items={scorecardModal.items}
                   meta={{ id: scorecardModal.id,
                           lab_name: scorecardModal.lab_name,
                           report_date: scorecardModal.report_date,
                           shared_at: scorecardModal.shared_at }}
                   onShare={async (id) => {
                     try {
                       const { data } = await api.post(`/tracker/scorecards/${id}/share`);
                       toast.success(`Shared with ${data.provider_count} provider${data.provider_count === 1 ? "" : "s"}`);
                       setScorecardModal((m) => m && ({ ...m, shared_at: data.shared_at }));
                       await load();
                     } catch (err) {
                       toast.error(err?.response?.data?.detail || "Could not share");
                     }
                   }}
                   onClose={() => setScorecardModal(null)} />
      )}

      <header className="border-b border-[#272E2B] pb-4">
        <div className="flex items-center gap-3">
          <Activity size={26} className="text-[#4ADE80]" />
          <h2 className="font-heading text-2xl">Lab Tracker</h2>
        </div>
        <p className="text-sm text-[#A3ACA7] mt-2">
          Track eGFR &amp; ACR with clinical data provenance. Upload a photo or PDF of your labs and Med Nexus AI translates every value into plain English with a color-coded scorecard.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Data entry panel */}
        <div className="ros-card md:col-span-1 h-fit" data-testid="lab-entry-card">
          <h3 className="font-heading text-lg text-white mb-4">Log new reading</h3>

          <form onSubmit={handleManualSubmit} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#A3ACA7] mb-1">Date</label>
              <input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                data-testid="lab-manual-date"
                className="w-full bg-[#1C201E] border border-[#272E2B] rounded-lg p-2.5 text-white focus:outline-none focus:border-[#4ADE80]"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#A3ACA7] mb-1">eGFR</label>
              <input
                type="number"
                step="0.1"
                value={newEgfr}
                onChange={(e) => setNewEgfr(e.target.value)}
                data-testid="lab-manual-egfr"
                className="w-full bg-[#1C201E] border border-[#272E2B] rounded-lg p-2.5 text-white focus:outline-none focus:border-[#4ADE80]"
                placeholder="e.g. 45"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#A3ACA7] mb-1">ACR (mg/g)</label>
              <input
                type="number"
                value={newAcr}
                onChange={(e) => setNewAcr(e.target.value)}
                data-testid="lab-manual-acr"
                className="w-full bg-[#1C201E] border border-[#272E2B] rounded-lg p-2.5 text-white focus:outline-none focus:border-[#4ADE80]"
                placeholder="e.g. 60"
              />
            </div>
            <button
              type="submit"
              data-testid="lab-manual-submit"
              className="w-full bg-[#4ADE80]/10 text-[#4ADE80] border border-[#4ADE80]/40 hover:bg-[#4ADE80] hover:text-black transition-colors rounded-full py-2.5 font-semibold flex items-center justify-center gap-2"
            >
              <Plus size={16} /> Add manual entry
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-[#272E2B]">
            <input
              type="file"
              ref={fileRef}
              accept="application/pdf,image/png,image/jpeg,image/webp,image/heic,image/heif"
              onChange={handleFileChange}
              className="hidden"
              data-testid="lab-pdf-file-input"
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="lab-pdf-upload-btn"
              className="w-full bg-[#1C201E] text-white hover:bg-[#272E2B] transition-colors rounded-full py-2.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {uploading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Parsing with AI…
                </>
              ) : (
                <>
                  <FileText size={16} /> <ImageIcon size={14} className="opacity-70" />
                  Upload lab (PDF or photo)
                </>
              )}
            </button>
            <p className="text-[11px] text-[#A3ACA7] text-center mt-2 leading-relaxed">
              PDFs are text-parsed; photos are read with our AI vision engine. Extracted values are marked <span className="text-[#4ADE80] font-semibold">Lab Verified</span> and a plain-English scorecard opens automatically.
            </p>
          </div>

          {scorecards.length > 0 && (
            <div className="mt-6 pt-6 border-t border-[#272E2B]">
              <div className="ros-label mb-2" style={{ fontSize: 9 }}>Recent scorecards</div>
              <div className="space-y-1.5">
                {scorecards.slice(0, 5).map((sc) => (
                  <button
                    key={sc.id}
                    data-testid={`recent-scorecard-${sc.id}`}
                    onClick={() => setScorecardModal({ id: sc.id, items: sc.items, lab_name: sc.lab_name, report_date: sc.report_date, shared_at: sc.shared_at })}
                    className="w-full text-left text-xs text-[#A3ACA7] hover:text-white bg-[#1C201E] hover:bg-[#272E2B] rounded-lg px-3 py-2 transition-colors flex items-center justify-between"
                  >
                    <span className="truncate">{sc.lab_name}</span>
                    <span className="text-[10px] text-[#A3ACA7] shrink-0 ml-2">{sc.report_date}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* History panel */}
        <div className="ros-card md:col-span-2 !p-0 overflow-hidden" data-testid="lab-history-card">
          <div className="p-6 border-b border-[#272E2B] bg-[#171c19] flex justify-between items-center">
            <h3 className="font-heading text-lg text-white">Clinical history</h3>
            <div className="flex gap-4 text-xs">
              <div className="flex items-center gap-1 text-[#4ADE80]">
                <ShieldCheck size={14} /> Verified
              </div>
              <div className="flex items-center gap-1 text-yellow-400">
                <User size={14} /> Self-reported
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#0f1311] text-[#A3ACA7] text-xs uppercase tracking-wider">
                  <th className="p-4 font-medium">Date</th>
                  <th className="p-4 font-medium">eGFR</th>
                  <th className="p-4 font-medium">ACR</th>
                  <th className="p-4 font-medium">Data source</th>
                  <th className="p-4 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#272E2B]">
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-[#A3ACA7]">
                      No readings yet. Log one manually or upload a lab PDF/photo.
                    </td>
                  </tr>
                )}
                {rows.map((row) => {
                  const primary = row.egfr || row.acr;
                  const isVerified = primary && primary.source === "lab_verified";
                  return (
                    <tr key={row.key} className="hover:bg-[#1a1f1c] transition-colors"
                        data-testid={`lab-row-${row.date}`}>
                      <td className="p-4 text-white/90 font-medium">{row.date}</td>
                      <td className="p-4">
                        <span className={`text-lg font-bold ros-metric ${row.egfr ? (row.egfr.source === "lab_verified" ? "text-white" : "text-[#A3ACA7]") : "text-[#3f4a44]"}`}>
                          {row.egfr ? row.egfr.egfr.toFixed(1) : "—"}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`text-lg font-semibold ros-metric ${row.acr ? (row.acr.source === "lab_verified" ? "text-white" : "text-[#A3ACA7]") : "text-[#3f4a44]"}`}>
                          {row.acr ? row.acr.acr : "—"}
                        </span>
                      </td>
                      <td className="p-4">
                        {isVerified ? (
                          <div className="flex flex-col">
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#4ADE80] bg-[#4ADE80]/10 px-2.5 py-1 rounded-md w-max border border-[#4ADE80]/30">
                              <ShieldCheck size={14} /> Lab verified
                            </span>
                            <span className="text-[11px] text-[#A3ACA7] mt-1 flex items-center gap-1">
                              <CheckCircle2 size={11} /> {primary.verified_by || "External lab"}
                            </span>
                          </div>
                        ) : (
                          <div className="flex flex-col">
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-yellow-400 bg-yellow-400/10 px-2.5 py-1 rounded-md w-max border border-yellow-400/30">
                              <User size={14} /> Patient reported
                            </span>
                            <span className="text-[11px] text-[#A3ACA7] mt-1 flex items-center gap-1">
                              <AlertCircle size={11} /> Unverified by clinic
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        {row.egfr && (
                          <button
                            onClick={() => deleteReading("egfr", row.egfr.id)}
                            className="text-[#A3ACA7] hover:text-red-400 p-1"
                            data-testid={`lab-delete-egfr-${row.egfr.id}`}
                            title="Delete eGFR"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
